import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  HttpError,
  type HttpClient,
  type RequestConfig,
} from '../models/httpClient.models';

export type Middleware = {
  request?: (
    config: InternalAxiosRequestConfig<unknown>
  ) =>
    | Promise<InternalAxiosRequestConfig<unknown>>
    | InternalAxiosRequestConfig<unknown>;
  response?: (
    res: AxiosResponse<unknown, unknown>
  ) =>
    | Promise<AxiosResponse<unknown, unknown>>
    | AxiosResponse<unknown, unknown>;
  errorHandler?: (error: AxiosError<unknown, unknown>) => unknown;
};

export type HttpClientOptions = {
  baseURL?: string;
  middleware?: Middleware;
};

export class AxiosHttpClient implements HttpClient {
  private axiosInstance: AxiosInstance;

  constructor(options?: HttpClientOptions) {
    const { baseURL, middleware } = options || {};

    this.axiosInstance = axios.create({ baseURL });

    if (middleware) {
      this.axiosInstance.interceptors.request.use((config) =>
        middleware.request ? middleware.request(config) : config
      );
      this.axiosInstance.interceptors.response.use((response) =>
        middleware.response ? middleware.response(response) : response
      );
    }

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error instanceof AxiosError) {
          if (middleware?.errorHandler) {
            middleware.errorHandler(error);
          }

          if (error.response) {
            throw new HttpError(
              error.response.statusText,
              error.response.status,
              {
                data: error.response.data,
                request: error.response.request,
                headers: error.response.headers,
              }
            );
          }
        } else if (error instanceof HttpError) {
          throw error;
        }
        throw new HttpError(error.message, 0, error);
      }
    );
  }
  async get<R>(url: string, config?: RequestConfig) {
    const response = await this.axiosInstance.get<R>(url, config);
    return this.handleResponse(response);
  }

  async post<R, T>(url: string, data?: T, config?: RequestConfig) {
    const response = await this.axiosInstance.post<R>(url, data, config);
    return this.handleResponse(response);
  }

  async put<R, T>(url: string, data?: T, config?: RequestConfig) {
    const response = await this.axiosInstance.put<R>(url, data, config);
    return this.handleResponse(response);
  }

  async patch<R, T>(url: string, data?: T, config?: RequestConfig) {
    const response = await this.axiosInstance.patch<R>(url, data, config);
    return this.handleResponse(response);
  }

  async delete(url: string, config?: RequestConfig): Promise<void> {
    const response = await this.axiosInstance.delete(url, config);
    return this.handleResponse(response);
  }

  private async handleResponse<R>(response: AxiosResponse<R>) {
    if (response.status < 300) {
      return response.data;
    }
    throw new HttpError(response.statusText, response.status, {
      data: response.data,
      request: response.request,
      headers: response.headers,
    });
  }
}
